import { useState, useEffect } from "react";

interface CompanyProfile {
  name: string;
  website: string;
  revenue: string;
  employees: number;
  holdingType: string;
  logoUrl: string;
  logoColor: string;
  textColor: string;
  keyOfferings: {
    description: string;
  };
  keyPeople: Array<{
    name: string;
    position: string;
    linkedin: string;
    profileImage: string;
  }>;
  stockPerformance: {
    peRatio: string;
    operatingMargin: string;
    marketCap: string;
    ebitda: string;
    chartUrl: string;
  };
  recentNews: Array<{
    title: string;
    description: string;
  }>;
  swotAnalysis: {
    strengths: Array<string>;
    weaknesses: Array<string>;
    opportunities: Array<string>;
    threats: Array<string>;
  };
}

export interface KeyCompetitorsCardProps {
  tenantId: string;
  tenantServiceId: string;
  tenantServiceOptionId: string;
}

// Complete data for all 10 header button companies
const competitorCompanies = [
  { 
    name: "Babcock and Wilson", 
    logoUrl: "https://via.placeholder.com/120x60/10B981/FFFFFF?text=B%26W",
    logoColor: "bg-green-100", 
    textColor: "text-green-700"
  },
  { 
    name: "Boyle Services", 
    logoUrl: "https://via.placeholder.com/120x60/3B82F6/FFFFFF?text=Boyle",
    logoColor: "bg-blue-100", 
    textColor: "text-blue-700"
  },
  { 
    name: "Fulton", 
    logoUrl: "https://via.placeholder.com/120x60/EF4444/FFFFFF?text=Fulton",
    logoColor: "bg-red-100", 
    textColor: "text-red-700"
  },
  { 
    name: "Miura America", 
    logoUrl: "https://via.placeholder.com/120x60/8B5CF6/FFFFFF?text=Miura",
    logoColor: "bg-purple-100", 
    textColor: "text-purple-700"
  },
  { 
    name: "Power Mechanical", 
    logoUrl: "https://via.placeholder.com/120x60/F59E0B/FFFFFF?text=Power",
    logoColor: "bg-orange-100", 
    textColor: "text-orange-700"
  },
  { 
    name: "Nationwide Boiler", 
    logoUrl: "https://via.placeholder.com/120x60/06B6D4/FFFFFF?text=NB",
    logoColor: "bg-cyan-100", 
    textColor: "text-cyan-700"
  },
  { 
    name: "John Wood Group", 
    logoUrl: "https://via.placeholder.com/120x60/84CC16/FFFFFF?text=Wood",
    logoColor: "bg-lime-100", 
    textColor: "text-lime-700"
  },
  { 
    name: "Bosch Industriekessel", 
    logoUrl: "https://via.placeholder.com/120x60/6366F1/FFFFFF?text=Bosch",
    logoColor: "bg-indigo-100", 
    textColor: "text-indigo-700"
  },
  { 
    name: "Thermodyne Boilers", 
    logoUrl: "https://via.placeholder.com/120x60/EC4899/FFFFFF?text=Thermo",
    logoColor: "bg-pink-100", 
    textColor: "text-pink-700"
  },
  { 
    name: "Siemens", 
    logoUrl: "https://via.placeholder.com/120x60/64748B/FFFFFF?text=Siemens",
    logoColor: "bg-gray-100", 
    textColor: "text-gray-700"
  }
];

// Mock data for all companies
const mockCompaniesData: Record<string, CompanyProfile> = {
  "Babcock and Wilson": {
    name: "Babcock and Wilson",
    website: "babcockandwilson.com",
    revenue: "$2.5 Bn",
    employees: 2511,
    holdingType: "Public",
    logoUrl: "https://via.placeholder.com/120x60/10B981/FFFFFF?text=B%26W",
    logoColor: "bg-green-100",
    textColor: "text-green-700",
    keyOfferings: {
      description: "Babcock and Wilson is a leading provider of boiler lifecycle solutions in North America, specializing in biogas enrichment systems that enable industrial boilers to operate using renewable biogas. Ideal for customers in the food and beverage sector, waste management, and other sustainability-driven industries."
    },
    keyPeople: [
      {
        name: "John Smith",
        position: "Chief Executive Officer",
        linkedin: "LinkedIn",
        profileImage: "https://randomuser.me/api/portraits/men/11.jpg"
      },
      {
        name: "Jane Doe", 
        position: "Chief Operating Officer",
        linkedin: "LinkedIn",
        profileImage: "https://randomuser.me/api/portraits/women/12.jpg"
      },
      {
        name: "Robert Johnson",
        position: "Chief Financial Officer",
        linkedin: "LinkedIn", 
        profileImage: "https://randomuser.me/api/portraits/men/13.jpg"
      }
    ],
    stockPerformance: {
      peRatio: "6.47",
      operatingMargin: "3.34%",
      marketCap: "$ 91.79 Mn",
      ebitda: "114.54",
      chartUrl: "https://quickchart.io/chart?c={type:'line',data:{labels:['Jan','Feb','Mar','Apr','May','Jun'],datasets:[{label:'Stock Price',data:[65,59,80,81,56,55],borderColor:'rgb(239,68,68)',backgroundColor:'rgba(239,68,68,0.1)',tension:0.4}]},options:{responsive:true,plugins:{legend:{display:false}},scales:{y:{beginAtZero:false}}}}"
    },
    recentNews: [
      {
        title: "Lorem Ipsum Sit Dolor Amet",
        description: "Unmatched full-lifecycle boiler services backed by OEM parts and the largest installed base ensures superior customer retention and rapid response"
      },
      {
        title: "Lorem Ipsum Sit Dolor Amet", 
        description: "Heavy reliance on large-scale industrial and utility clients exposes B&W to project timing volatility and high fixed-cost structure"
      },
      {
        title: "Lorem Ipsum Sit Dolor Amet",
        description: "Heightened demand for emissions-control retrofits and green-energy solutions presents significant aftermarket growth potential"
      }
    ],
    swotAnalysis: {
      strengths: [
        "Unmatched full-lifecycle boiler services backed by OEM parts and the largest installed base ensures superior customer retention and rapid response"
      ],
      weaknesses: [
        "Heavy reliance on large-scale industrial and utility clients exposes B&W to project timing volatility and high fixed-cost structure"
      ],
      opportunities: [
        "Heightened demand for emissions-control retrofits and green-energy solutions presents significant aftermarket growth potential"
      ],
      threats: [
        "Increasing regulatory pressure on traditional fossil fuel systems may impact long-term market demand"
      ]
    }
  },
  "Boyle Services": {
    name: "Boyle Services",
    website: "boyleservices.com",
    revenue: "$1.8 Bn",
    employees: 1850,
    holdingType: "Private",
    logoUrl: "https://via.placeholder.com/120x60/3B82F6/FFFFFF?text=Boyle",
    logoColor: "bg-blue-100",
    textColor: "text-blue-700",
    keyOfferings: {
      description: "Boyle Services specializes in comprehensive heating, ventilation, and air conditioning solutions for commercial and industrial facilities. With over 40 years of experience, they provide energy-efficient HVAC systems, maintenance services, and custom installation solutions."
    },
    keyPeople: [
      {
        name: "Michael Boyle",
        position: "Chief Executive Officer",
        linkedin: "LinkedIn",
        profileImage: "https://randomuser.me/api/portraits/men/21.jpg"
      },
      {
        name: "Sarah Johnson", 
        position: "Chief Technology Officer",
        linkedin: "LinkedIn",
        profileImage: "https://randomuser.me/api/portraits/women/22.jpg"
      },
      {
        name: "David Wilson",
        position: "Chief Financial Officer",
        linkedin: "LinkedIn", 
        profileImage: "https://randomuser.me/api/portraits/men/23.jpg"
      }
    ],
    stockPerformance: {
      peRatio: "8.2",
      operatingMargin: "4.1%",
      marketCap: "$ 156.3 Mn",
      ebitda: "89.7",
      chartUrl: "https://quickchart.io/chart?c={type:'line',data:{labels:['Jan','Feb','Mar','Apr','May','Jun'],datasets:[{label:'Stock Price',data:[45,52,48,61,58,62],borderColor:'rgb(59,130,246)',backgroundColor:'rgba(59,130,246,0.1)',tension:0.4}]},options:{responsive:true,plugins:{legend:{display:false}},scales:{y:{beginAtZero:false}}}}"
    },
    recentNews: [
      {
        title: "Lorem Ipsum Sit Dolor Amet",
        description: "Strategic acquisition expands market presence in southeastern United States with enhanced service capabilities"
      },
      {
        title: "Lorem Ipsum Sit Dolor Amet", 
        description: "New energy efficiency program launches comprehensive energy audit services for commercial buildings"
      }
    ],
    swotAnalysis: {
      strengths: [
        "Strong regional market presence with established customer relationships and comprehensive service offerings"
      ],
      weaknesses: [
        "Limited national footprint compared to larger competitors in the HVAC industry"
      ],
      opportunities: [
        "Growing demand for energy-efficient HVAC systems and smart building technologies"
      ],
      threats: [
        "Increased competition from national HVAC chains and technology disruptors"
      ]
    }
  },
  "Fulton": {
    name: "Fulton",
    website: "fulton.com",
    revenue: "$3.1 Bn",
    employees: 3200,
    holdingType: "Public",
    logoUrl: "https://via.placeholder.com/120x60/EF4444/FFFFFF?text=Fulton",
    logoColor: "bg-red-100",
    textColor: "text-red-700",
    keyOfferings: {
      description: "Fulton is a leading manufacturer of steam, hot water, and thermal fluid heat transfer systems. The company designs and manufactures boilers, burners, and related equipment for commercial, institutional, and light industrial applications worldwide."
    },
    keyPeople: [
      {
        name: "Robert Fulton",
        position: "Chairman & CEO",
        linkedin: "LinkedIn",
        profileImage: "https://randomuser.me/api/portraits/men/31.jpg"
      },
      {
        name: "Lisa Chen", 
        position: "President",
        linkedin: "LinkedIn",
        profileImage: "https://randomuser.me/api/portraits/women/32.jpg"
      }
    ],
    stockPerformance: {
      peRatio: "12.5",
      operatingMargin: "5.8%",
      marketCap: "$ 285.4 Mn",
      ebitda: "156.8",
      chartUrl: "https://quickchart.io/chart?c={type:'line',data:{labels:['Jan','Feb','Mar','Apr','May','Jun'],datasets:[{label:'Stock Price',data:[55,53,57,59,68,72],borderColor:'rgb(239,68,68)',backgroundColor:'rgba(239,68,68,0.1)',tension:0.4}]},options:{responsive:true,plugins:{legend:{display:false}},scales:{y:{beginAtZero:false}}}}"
    },
    recentNews: [
      {
        title: "Lorem Ipsum Sit Dolor Amet",
        description: "Advanced control technology reduces energy consumption by 15% while maintaining optimal performance standards"
      }
    ],
    swotAnalysis: {
      strengths: [
        "Industry-leading innovation in steam and thermal systems with strong brand recognition globally"
      ],
      weaknesses: [
        "Heavy dependence on cyclical construction and industrial markets affecting revenue stability"
      ],
      opportunities: [
        "Expanding markets for energy-efficient heating systems and industrial process applications"
      ],
      threats: [
        "Economic downturns impacting capital equipment purchases and construction projects"
      ]
    }
  },
  "Miura America": {
    name: "Miura America",
    website: "miuraamerica.com",
    revenue: "$1.2 Bn",
    employees: 980,
    holdingType: "Private",
    logoUrl: "https://via.placeholder.com/120x60/8B5CF6/FFFFFF?text=Miura",
    logoColor: "bg-purple-100",
    textColor: "text-purple-700",
    keyOfferings: {
      description: "Miura America specializes in compact, energy-efficient steam boilers for various industries. Known for innovative water-tube technology that provides rapid steam generation, precise control, and exceptional fuel efficiency."
    },
    keyPeople: [
      {
        name: "Takeshi Miura",
        position: "President & CEO",
        linkedin: "LinkedIn",
        profileImage: "https://randomuser.me/api/portraits/men/41.jpg"
      }
    ],
    stockPerformance: {
      peRatio: "9.8",
      operatingMargin: "6.2%",
      marketCap: "$ 78.5 Mn",
      ebitda: "42.1",
      chartUrl: "https://quickchart.io/chart?c={type:'line',data:{labels:['Jan','Feb','Mar','Apr','May','Jun'],datasets:[{label:'Stock Price',data:[60,62,61,65,64,67],borderColor:'rgb(139,92,246)',backgroundColor:'rgba(139,92,246,0.1)',tension:0.4}]},options:{responsive:true,plugins:{legend:{display:false}},scales:{y:{beginAtZero:false}}}}"
    },
    recentNews: [
      {
        title: "Lorem Ipsum Sit Dolor Amet",
        description: "Revolutionary space-saving boiler design for urban facilities with limited space requirements"
      }
    ],
    swotAnalysis: {
      strengths: [
        "Innovative compact boiler technology with superior energy efficiency and space utilization"
      ],
      weaknesses: [
        "Smaller market share in North America compared to established local competitors"
      ],
      opportunities: [
        "Growing demand for space-efficient and energy-saving industrial equipment"
      ],
      threats: [
        "Trade tensions and tariffs affecting international equipment imports"
      ]
    }
  },
  "Power Mechanical": {
    name: "Power Mechanical",
    website: "powermechanical.com",
    revenue: "$2.8 Bn",
    employees: 2400,
    holdingType: "Public",
    logoUrl: "https://via.placeholder.com/120x60/F59E0B/FFFFFF?text=Power",
    logoColor: "bg-orange-100",
    textColor: "text-orange-700",
    keyOfferings: {
      description: "Power Mechanical provides comprehensive mechanical contracting services including HVAC, plumbing, and fire protection systems for commercial and industrial projects."
    },
    keyPeople: [
      {
        name: "Thomas Power",
        position: "Chief Executive Officer",
        linkedin: "LinkedIn",
        profileImage: "https://randomuser.me/api/portraits/men/51.jpg"
      }
    ],
    stockPerformance: {
      peRatio: "11.3",
      operatingMargin: "4.7%",
      marketCap: "$ 198.2 Mn",
      ebitda: "125.6",
      chartUrl: "https://quickchart.io/chart?c={type:'line',data:{labels:['Jan','Feb','Mar','Apr','May','Jun'],datasets:[{label:'Stock Price',data:[58,56,62,65,63,68],borderColor:'rgb(245,158,11)',backgroundColor:'rgba(245,158,11,0.1)',tension:0.4}]},options:{responsive:true,plugins:{legend:{display:false}},scales:{y:{beginAtZero:false}}}}"
    },
    recentNews: [
      {
        title: "Lorem Ipsum Sit Dolor Amet",
        description: "Major contract awarded for sustainable building systems in metropolitan area development project"
      }
    ],
    swotAnalysis: {
      strengths: [
        "Diverse service portfolio with strong project management capabilities"
      ],
      weaknesses: [
        "Dependence on construction industry cycles"
      ],
      opportunities: [
        "Growing market for sustainable building technologies"
      ],
      threats: [
        "Material cost inflation affecting project profitability"
      ]
    }
  },
  // Add remaining companies with similar structure...
  "Nationwide Boiler": {
    name: "Nationwide Boiler",
    website: "nationwideboiler.com",
    revenue: "$1.5 Bn",
    employees: 1200,
    holdingType: "Private",
    logoUrl: "https://via.placeholder.com/120x60/06B6D4/FFFFFF?text=NB",
    logoColor: "bg-cyan-100",
    textColor: "text-cyan-700",
    keyOfferings: {
      description: "Nationwide Boiler offers temporary and permanent boiler solutions including rental, sales, and service. Specializes in emergency boiler rentals and turnkey boiler room solutions."
    },
    keyPeople: [
      {
        name: "Patricia Wilson",
        position: "Chief Executive Officer",
        linkedin: "LinkedIn",
        profileImage: "https://randomuser.me/api/portraits/women/61.jpg"
      }
    ],
    stockPerformance: {
      peRatio: "7.9",
      operatingMargin: "5.2%",
      marketCap: "$ 89.7 Mn",
      ebitda: "76.3",
      chartUrl: "https://quickchart.io/chart?c={type:'line',data:{labels:['Jan','Feb','Mar','Apr','May','Jun'],datasets:[{label:'Stock Price',data:[42,45,41,48,52,49],borderColor:'rgb(6,182,212)',backgroundColor:'rgba(6,182,212,0.1)',tension:0.4}]},options:{responsive:true,plugins:{legend:{display:false}},scales:{y:{beginAtZero:false}}}}"
    },
    recentNews: [
      {
        title: "Lorem Ipsum Sit Dolor Amet",
        description: "Fleet expansion program adds 50 new rental boilers to support growing emergency service demand"
      }
    ],
    swotAnalysis: {
      strengths: [
        "Largest rental boiler fleet in North America with rapid emergency response capabilities"
      ],
      weaknesses: [
        "High capital requirements for fleet maintenance"
      ],
      opportunities: [
        "Increasing demand for temporary boiler solutions"
      ],
      threats: [
        "Economic slowdowns reducing rental demand"
      ]
    }
  },
  "John Wood Group": {
    name: "John Wood Group",
    website: "johnwoodgroup.com",
    revenue: "$6.2 Bn",
    employees: 35000,
    holdingType: "Public",
    logoUrl: "https://via.placeholder.com/120x60/84CC16/FFFFFF?text=Wood",
    logoColor: "bg-lime-100",
    textColor: "text-lime-700",
    keyOfferings: {
      description: "John Wood Group is a global leader in consulting, project delivery and operations solutions in energy and the built environment."
    },
    keyPeople: [
      {
        name: "Robin Watson",
        position: "Chief Executive Officer",
        linkedin: "LinkedIn",
        profileImage: "https://randomuser.me/api/portraits/men/71.jpg"
      }
    ],
    stockPerformance: {
      peRatio: "15.4",
      operatingMargin: "3.8%",
      marketCap: "$ 1.2 Bn",
      ebitda: "285.7",
      chartUrl: "https://quickchart.io/chart?c={type:'line',data:{labels:['Jan','Feb','Mar','Apr','May','Jun'],datasets:[{label:'Stock Price',data:[125,118,132,128,135,142],borderColor:'rgb(132,204,22)',backgroundColor:'rgba(132,204,22,0.1)',tension:0.4}]},options:{responsive:true,plugins:{legend:{display:false}},scales:{y:{beginAtZero:false}}}}"
    },
    recentNews: [
      {
        title: "Lorem Ipsum Sit Dolor Amet",
        description: "Major renewable energy project contract secured in offshore wind development sector"
      }
    ],
    swotAnalysis: {
      strengths: [
        "Global presence with diversified service portfolio across energy sectors"
      ],
      weaknesses: [
        "Exposure to volatile oil and gas markets"
      ],
      opportunities: [
        "Energy transition creating demand for renewable services"
      ],
      threats: [
        "Geopolitical tensions impacting international projects"
      ]
    }
  },
  "Bosch Industriekessel": {
    name: "Bosch Industriekessel",
    website: "boschindustriekessel.com",
    revenue: "$4.8 Bn",
    employees: 8500,
    holdingType: "Private",
    logoUrl: "https://via.placeholder.com/120x60/6366F1/FFFFFF?text=Bosch",
    logoColor: "bg-indigo-100",
    textColor: "text-indigo-700",
    keyOfferings: {
      description: "Bosch Industriekessel manufactures industrial steam and hot water boilers for various applications. Part of the Bosch Group, specializing in energy-efficient heating solutions."
    },
    keyPeople: [
      {
        name: "Klaus Mueller",
        position: "Managing Director",
        linkedin: "LinkedIn",
        profileImage: "https://randomuser.me/api/portraits/men/81.jpg"
      }
    ],
    stockPerformance: {
      peRatio: "13.7",
      operatingMargin: "6.1%",
      marketCap: "$ 324.8 Mn",
      ebitda: "198.4",
      chartUrl: "https://quickchart.io/chart?c={type:'line',data:{labels:['Jan','Feb','Mar','Apr','May','Jun'],datasets:[{label:'Stock Price',data:[88,92,85,94,98,102],borderColor:'rgb(99,102,241)',backgroundColor:'rgba(99,102,241,0.1)',tension:0.4}]},options:{responsive:true,plugins:{legend:{display:false}},scales:{y:{beginAtZero:false}}}}"
    },
    recentNews: [
      {
        title: "Lorem Ipsum Sit Dolor Amet",
        description: "Next-generation condensing boiler technology achieves industry-leading efficiency ratings"
      }
    ],
    swotAnalysis: {
      strengths: [
        "Strong German engineering reputation with advanced R&D resources"
      ],
      weaknesses: [
        "Higher cost structure compared to emerging market competitors"
      ],
      opportunities: [
        "Growing demand for high-efficiency industrial heating systems"
      ],
      threats: [
        "Increasing competition from lower-cost manufacturers"
      ]
    }
  },
  "Thermodyne Boilers": {
    name: "Thermodyne Boilers",
    website: "thermodyneboilers.com",
    revenue: "$0.8 Bn",
    employees: 650,
    holdingType: "Public",
    logoUrl: "https://via.placeholder.com/120x60/EC4899/FFFFFF?text=Thermo",
    logoColor: "bg-pink-100",
    textColor: "text-pink-700",
    keyOfferings: {
      description: "Thermodyne Boilers is an Indian manufacturer of industrial boilers, thermic fluid heaters, and steam generators providing energy-efficient heating solutions."
    },
    keyPeople: [
      {
        name: "Rajesh Sharma",
        position: "Managing Director",
        linkedin: "LinkedIn",
        profileImage: "https://randomuser.me/api/portraits/men/91.jpg"
      }
    ],
    stockPerformance: {
      peRatio: "18.2",
      operatingMargin: "8.3%",
      marketCap: "$ 42.6 Mn",
      ebitda: "58.9",
      chartUrl: "https://quickchart.io/chart?c={type:'line',data:{labels:['Jan','Feb','Mar','Apr','May','Jun'],datasets:[{label:'Stock Price',data:[35,38,33,41,45,42],borderColor:'rgb(236,72,153)',backgroundColor:'rgba(236,72,153,0.1)',tension:0.4}]},options:{responsive:true,plugins:{legend:{display:false}},scales:{y:{beginAtZero:false}}}}"
    },
    recentNews: [
      {
        title: "Lorem Ipsum Sit Dolor Amet",
        description: "Export market expansion program targets Southeast Asian industrial manufacturing sector"
      }
    ],
    swotAnalysis: {
      strengths: [
        "Cost-effective manufacturing with competitive pricing in emerging markets"
      ],
      weaknesses: [
        "Limited global brand recognition compared to international competitors"
      ],
      opportunities: [
        "Rapid industrialization in developing countries creating demand"
      ],
      threats: [
        "Environmental regulations requiring higher technology standards"
      ]
    }
  },
  "Siemens": {
    name: "Siemens",
    website: "siemens.com",
    revenue: "$78.5 Bn",
    employees: 293000,
    holdingType: "Public",
    logoUrl: "https://via.placeholder.com/120x60/64748B/FFFFFF?text=Siemens",
    logoColor: "bg-gray-100",
    textColor: "text-gray-700",
    keyOfferings: {
      description: "Siemens is a global technology powerhouse focusing on industry, infrastructure, transport, and healthcare. In energy sector, provides comprehensive solutions from power generation to grid management."
    },
    keyPeople: [
      {
        name: "Roland Busch",
        position: "President & CEO",
        linkedin: "LinkedIn",
        profileImage: "https://randomuser.me/api/portraits/men/101.jpg"
      }
    ],
    stockPerformance: {
      peRatio: "16.8",
      operatingMargin: "11.2%",
      marketCap: "$ 89.4 Bn",
      ebitda: "9854.3",
      chartUrl: "https://quickchart.io/chart?c={type:'line',data:{labels:['Jan','Feb','Mar','Apr','May','Jun'],datasets:[{label:'Stock Price',data:[158,152,165,172,168,175],borderColor:'rgb(100,116,139)',backgroundColor:'rgba(100,116,139,0.1)',tension:0.4}]},options:{responsive:true,plugins:{legend:{display:false}},scales:{y:{beginAtZero:false}}}}"
    },
    recentNews: [
      {
        title: "Lorem Ipsum Sit Dolor Amet",
        description: "Digital transformation initiative launches comprehensive Industry 4.0 solutions portfolio"
      }
    ],
    swotAnalysis: {
      strengths: [
        "Diversified technology portfolio with strong R&D capabilities and global market presence"
      ],
      weaknesses: [
        "Complex organizational structure and dependence on cyclical industrial markets"
      ],
      opportunities: [
        "Digital transformation and sustainability trends driving demand for smart technology solutions"
      ],
      threats: [
        "Geopolitical tensions affecting international business and supply chain disruptions"
      ]
    }
  }
};export const KeyCompetitorCard = ({ tenantId, tenantServiceId, tenantServiceOptionId }: KeyCompetitorsCardProps) => {
    const [selectedCompany, setSelectedCompany] = useState("Babcock and Wilson");
    const [data, setData] = useState<Record<string, CompanyProfile> | null>(null);
    const [loading, setLoading] = useState(true);
  
    useEffect(() => {
      if (!tenantId || !tenantServiceId || !tenantServiceOptionId) return;
  
      const loadMockData = async () => {
        try {
          await new Promise(resolve => setTimeout(resolve, 1000));
          console.log("Loading key competitors mock data", { tenantId, tenantServiceId, tenantServiceOptionId });
          setData(mockCompaniesData);
        } catch (err) {
          console.error("Failed to load key competitors data:", err);
        } finally {
          setLoading(false);
        }
      };
  
      loadMockData();
    }, [tenantId, tenantServiceId, tenantServiceOptionId]);
  
    if (loading)
      return <p className="text-sm text-gray-500">Loading key competitors data...</p>;
    if (!data)
      return <p className="text-sm text-red-500">Failed to load data.</p>;
  
    const currentCompany = data[selectedCompany] || data["Babcock and Wilson"];
    const currentCompanyButton = competitorCompanies.find(c => c.name === selectedCompany) || competitorCompanies[0];
  
    return (
      <div className="bg-[#FDFDFD] rounded-xl max-w-4xl m-auto mt-0 2xl:mt-20  shadow-sm border border-gray-100 ">
        
        {/* Header Section */}
        <div className="p-4 bg-[#FAFBFD] rounded-t-2xl">
          
          {/* Company Logo Buttons */}
          <div className="flex flex-wrap gap-2 mb-6">
            {competitorCompanies.map((company, index) => (
              <button
                key={index}
                onClick={() => setSelectedCompany(company.name)}
                className={`flex items-center gap-2 px-3 py-2 rounded-3xl text-xs text-light transition-colors ${
                  selectedCompany === company.name
                    ? 'bg-gray-100 text-[#1444FF] border-gray-200'
                    : 'bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100'
                }`}
              >
                <img 
                  src={company.logoUrl} 
                  alt={company.name}
                  className="w-4 h-2 object-contain rounded flex-shrink-0"
                />
                <span className="whitespace-nowrap">{company.name}</span>
              </button>
            ))}
          </div>
  
          {/* Selected Company Details */}
          <div className="p-6">
            <div className="flex items-start gap-4 mb-4">
              {/* Large Company Logo */}
              <div className="w-20 h-20 bg-gray-50 rounded-lg flex items-center justify-center flex-shrink-0 p-2">
                <img 
                  src={currentCompanyButton.logoUrl} 
                  alt={currentCompany.name} 
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              
              {/* Company Info */}
              <div className="flex-1">
                <div className="flex flex-col gap-1 ml-10">
                  {/* Company Name in Header */}
                  <h2 className="text-xl font-light text-gray-900">
                    {currentCompany.name}
                  </h2>
                  
                  {/* Go to Website */}
                  <a href={`https://${currentCompany.website}`} target="_blank" rel="noopener noreferrer"
                     className="text-sm text-[#1444FF] hover:text-blue-800 pb-10">
                    Go to Website
                  </a>
                  
                  {/* Revenue, Employees, Holding Type in Single Row */}
                  <div className="flex items-center gap-50 text-sm mt-2 whitespace-nowrap">
                    <div>
                      <span className="text-gray-500">Revenue (2025): </span>
                      <div className="font-light text-gray-700">{currentCompany.revenue}</div>
                    </div>
                    <div>
                      <span className="text-gray-500">Employees: </span>
                      <div className="font-light text-gray-700">{currentCompany.employees.toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Holding type: </div>
                      <span className="font-light text-gray-700">{currentCompany.holdingType}</span>
                      <a href="#" className="text-[#1444FF] hover:text-blue-800 ml-2 text-xs">Yahoo Finance</a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
  
        {/* All Sections - Scrollable Content */}
        <div className="space-y-6 p-6">
          
          {/* Key Offerings Section - NO BORDER */}
          <div className="pt-4 flex pb-4">
            <p className="text-sm font-light text-gray-500 mb-1 min-w-[150px] whitespace-nowrap">
              Key Offerings
            </p>
            <div className="flex-1">
              <p className="text-sm text-gray-500 leading-relaxed mb-4">
                {currentCompany.keyOfferings.description}
              </p>
              <button className="bg-white text-gray-700 text-xs px-4 py-2 rounded-2xl shadow-md hover:shadow-lg transition-shadow border border-gray-200">
                Trustpilot
              </button>
            </div>
          </div>
  
          {/* Key People Section - NO BORDER, but borders for each person */}
          <div className="pt-4 flex pb-4">
            <p className="text-sm font-light text-gray-500 mb-1 min-w-[150px] whitespace-nowrap">
              Key People
            </p>
            <div className="flex-1">
              <div className="space-y-0">
                {currentCompany.keyPeople.map((person, index) => (
                  <div key={index} className="grid grid-cols-3 gap-4 items-center border-b border-gray-100 pb-3 pt-3 last:border-b-0">
                    <div className="flex items-center gap-3">
                      <img 
                        src={person.profileImage} 
                        alt={person.name}
                        className="w-7 h-7 rounded-full"
                      />
                      <p className="text-sm font-light text-gray-900">{person.name}</p>
                    </div>
                    <div>
                      <p className="text-xs" style={{ color: '#64738B' }}>{person.position}</p>
                    </div>
                    <div>
                      <a href="#" className="text-blue-600 text-xs hover:text-blue-800">
                        {person.linkedin}
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
  
          {/* Stock Performance Section - NO BORDER, but borders for each metric */}
          <div className="pt-4 flex pb-4">
            <p className="text-sm font-light text-gray-500 mb-1 min-w-[150px]">
              Stock Performance
            </p>
            <div className="flex-1">
              <div className="w-full mb-4">
                <img 
                  src={currentCompany.stockPerformance.chartUrl}
                  alt="Stock Performance Chart"
                  className="w-full h-40 rounded-lg border border-gray-200"
                />
              </div>
  
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-0">
                  <div className="flex items-center gap-2 border-b border-gray-100 pb-3 pt-3">
                    <span className="text-xs text-gray-500">P/E Ratio</span>
                    <span className="text-sm font-medium text-gray-900">{currentCompany.stockPerformance.peRatio}</span>
                  </div>
                  <div className="flex items-center gap-2 pb-3 pt-3">
                    <span className="text-xs text-gray-500">Market Cap</span>
                    <span className="text-sm font-medium text-gray-900">{currentCompany.stockPerformance.marketCap}</span>
                  </div>
                </div>
                <div className="space-y-0">
                  <div className="flex items-center gap-2 border-b border-gray-100 pb-3 pt-3">
                    <span className="text-xs text-gray-500">Operating Margin</span>
                    <span className="text-sm font-medium text-gray-900">{currentCompany.stockPerformance.operatingMargin}</span>
                  </div>
                  <div className="flex items-center gap-2 pb-3 pt-3">
                    <span className="text-xs text-gray-500">EBITDA</span>
                    <span className="text-sm font-medium text-gray-900">{currentCompany.stockPerformance.ebitda}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
  
          {/* Recent News Section - NO BORDER, but borders for each news item */}
          <div className="pt-4 flex pb-4">
            <p className="text-sm font-medium text-gray-500 mb-1 min-w-[150px]">
              Recent News
            </p>
            <div className="flex-1">
              <div className="space-y-0">
                {currentCompany.recentNews.map((news, index) => (
                  <div key={index} className="grid grid-cols-2 gap-4 border-b border-gray-100 pb-3 pt-3 last:border-b-0">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">{news.title}</h4>
                    </div>
                    <div>
                      <p className="text-sm leading-relaxed" style={{ color: '#64738B' }}>{news.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
  
          {/* SWOT Analysis Section - NO BORDER, but borders for each SWOT row */}
          <div className="pt-4 flex pb-4">
            <p className="text-sm font-medium text-gray-500 mb-1 min-w-[150px]">
              SWOT Analysis
            </p>
            <div className="flex-1">
              <div className="space-y-0">
                <div className="grid grid-cols-2 gap-4 border-b border-gray-100 pb-3 pt-3">
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-2">Strengths</h4>
                  </div>
                  <div>
                    {currentCompany.swotAnalysis.strengths.map((strength, index) => (
                      <p key={index} className="text-sm leading-relaxed mb-2" style={{ color: '#64738B' }}>{strength}</p>
                    ))}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 border-b border-gray-100 pb-3 pt-3">
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-2">Weaknesses</h4>
                  </div>
                  <div>
                    {currentCompany.swotAnalysis.weaknesses.map((weakness, index) => (
                      <p key={index} className="text-sm leading-relaxed mb-2" style={{ color: '#64738B' }}>{weakness}</p>
                    ))}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 border-b border-gray-100 pb-3 pt-3">
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-2">Opportunities</h4>
                  </div>
                  <div>
                    {currentCompany.swotAnalysis.opportunities.map((opportunity, index) => (
                      <p key={index} className="text-sm leading-relaxed mb-2" style={{ color: '#64738B' }}>{opportunity}</p>
                    ))}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 pb-3 pt-3">
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-2">Threats</h4>
                  </div>
                  <div>
                    {currentCompany.swotAnalysis.threats.map((threat, index) => (
                      <p key={index} className="text-sm leading-relaxed mb-2" style={{ color: '#64738B' }}>{threat}</p>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
  
        </div>
      </div>
    );
  };
  